import { useQuery } from "@tanstack/react-query";
import { catalogApiClient } from "../services/catalogApiClient";
import { CatalogFieldHistoryControllerApi, type FieldHistoryEntryDto } from "@/lib/api/generated/casedb";
import { presentCatalogError } from "../utils/catalogErrorPresenter";
import {
  FIELD_HISTORY_ACTOR_LABELS,
  FIELD_HISTORY_CLEARED_LABEL,
  FIELD_HISTORY_DEFAULT_ACTOR_LABEL,
  FIELD_HISTORY_NOT_SET_LABEL,
  FIELD_HISTORY_PII_CHANGED_LABEL,
  FIELD_HISTORY_PII_LABEL,
  FIELD_HISTORY_QUERY_KEY,
  FIELD_HISTORY_UNKNOWN_STATE_NOTE,
  FIELD_HISTORY_UNKNOWN_VALUE_LABEL,
} from "../constants/caseReviewLabels";

export type FieldHistoryStatus = "idle" | "loading" | "loaded" | "failed";

/** Internal audit writer identifier; keep it in the API, not in the review popup. */
const JAVA_AUDIT_SOURCE = "java";

/** One change, already reduced to the four things a reviewer reads: who, when, from → to, source. */
export interface FieldHistoryEntry {
  id: string;
  when: string;
  who: string;
  from: string;
  to: string;
  isPii: boolean;
  source?: string;
  reason?: string;
}

export interface FieldHistory {
  status: FieldHistoryStatus;
  entries: FieldHistoryEntry[];
  error: string | undefined;
}

/** Same header fix as `catalogSectionService.ts`: browsers refuse the generated client's own User-Agent header. */


async function historyApi(): Promise<CatalogFieldHistoryControllerApi> {
  return catalogApiClient(CatalogFieldHistoryControllerApi);
}

function who(entry: FieldHistoryEntryDto): string {
  const label = FIELD_HISTORY_ACTOR_LABELS[entry.actorKind ?? ""] ?? FIELD_HISTORY_DEFAULT_ACTOR_LABEL;
  const parts = [label, entry.actorName, entry.stateUnavailable ? FIELD_HISTORY_UNKNOWN_STATE_NOTE : undefined];
  return parts.filter(Boolean).join(" · ");
}

/** How a field shows a stored value, when the stored text is not what a reviewer reads (a criteria document). */
export type FieldHistoryFormat = (stored: string) => string;

/** The value side of a change; PII never reaches the screen, only that it changed. */
function toDisplay(entry: FieldHistoryEntryDto, format: FieldHistoryFormat): string {
  if (entry.valueClass === "PII") return `${FIELD_HISTORY_PII_LABEL} — ${entry.newDisplay ?? FIELD_HISTORY_PII_CHANGED_LABEL}`;
  return entry.newDisplay != null ? format(entry.newDisplay) : FIELD_HISTORY_CLEARED_LABEL;
}

function fromDisplay(entry: FieldHistoryEntryDto, format: FieldHistoryFormat): string {
  if (entry.valueClass === "PII") return FIELD_HISTORY_PII_LABEL;
  if (entry.oldDisplay != null) return format(entry.oldDisplay);
  return entry.action === "INSERT" ? FIELD_HISTORY_NOT_SET_LABEL : FIELD_HISTORY_UNKNOWN_VALUE_LABEL;
}

const AS_STORED: FieldHistoryFormat = (stored) => stored;

function prepareEntry(entry: FieldHistoryEntryDto, index: number, format: FieldHistoryFormat): FieldHistoryEntry {
  return {
    id: `${entry.changedAt ?? "unknown"}-${index}`,
    when: entry.changedAt ? new Date(entry.changedAt).toLocaleString() : "",
    who: who(entry),
    from: fromDisplay(entry, format),
    to: toDisplay(entry, format),
    isPii: entry.valueClass === "PII",
    source: entry.source === JAVA_AUDIT_SOURCE ? undefined : entry.source,
    reason: entry.reason,
  };
}

/**
 * "How did this value get here?" — every change to one field on one record, newest first, over the
 * generated `catalogFieldHistory` operation (record_id is a required query param, D71a).
 *
 * <p>Lazy: nothing fetches until `enabled` is true, so the popover that owns this hook can defer
 * the request until the reviewer actually opens it.
 *
 * <p>Keyed rather than per-mount. The request used to be a raw effect, which meant one request per
 * mount of the component that hosts it — and opening a single popover mounts it more than once:
 * StrictMode invokes every effect twice in development, and the popover's own content can render
 * more than once besides. Two identical GETs for one open, measured; four in a colleague's run.
 * Keying the read by case, field and record collapses those to one in flight and serves a reopen
 * from cache, which is also what makes the laziness worth having.
 */
/**
 * @param recordId the row the value lives on. Required for a field inside a collection, which the
 *     client already identifies; omitted for a scalar field, whose row nothing in the review
 *     document names and the server resolves from the case.
 * @param format how the field shows a stored value; one field always shows its values the same way, so the cached
 *     entries stay right. Stored text as it is when absent.
 */
export function useCatalogFieldHistory(caseId: number, fieldId: string, recordId: string | undefined, enabled: boolean,
                                       format: FieldHistoryFormat = AS_STORED): FieldHistory {
  // Keyed rather than hand-rolled, because one popover opening is not one mount. React runs every
  // effect twice under StrictMode and the popover renders its content more than once, so the effect
  // this used to be sent four identical GETs for a single open. A query key collapses concurrent
  // mounts into one request; `staleTime` is what makes the mount-unmount-remount StrictMode does —
  // and a genuine re-open — read the cache instead of asking again.
  const query = useQuery({
    queryKey: [FIELD_HISTORY_QUERY_KEY, caseId, fieldId, recordId ?? null],
    enabled,
    // Reuse history until a successful Save cancels older requests and invalidates the case prefix.
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      const client = await historyApi();
      const response = await client.catalogFieldHistory(caseId, fieldId, recordId);
      return (response.data.data?.entries ?? []).map((entry, index) => prepareEntry(entry, index, format));
    },
  });

  if (!enabled) return { status: "idle", entries: [], error: undefined };
  if (query.isPending) return { status: "loading", entries: [], error: undefined };
  if (query.isError) {
    return { status: "failed", entries: [], error: presentCatalogError(query.error).message };
  }
  return { status: "loaded", entries: query.data ?? [], error: undefined };
}
